import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import React, { useEffect, useState, useCallback } from "react";
import DOMPurify from 'dompurify';
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { fetchPublicCampaign } from "../../services/api/campaign";
import CampaignImageSlider from "../../components/campaign/CampaignImageSlider";
import StrategyIcon from "../../components/icons/StrategyIcon";
import Loader from "../../components/loaders/Loader";
import usePageTitle from "../../hooks/usePageTitle";
import { RootState } from "../../redux-stores/store";
import { CampaignData } from "../../types/sponsorships/campaigns";
import { arrayOfUniqueStrings, textToTitleCase, sanitizeURLs, toValidNumber } from "../../utils/helpers";
import { getLocalStorageItem, setLocalStorageItem } from "../../utils/localStorage";
import { platforms } from "../../constants/constants";
import { CampaignSpecsRow, ShowMoreContentModal } from "../Campaign";


import instagramLogo from "../../assets/icons/instagram-logo.svg";
import youtubeLogo from "../../assets/icons/youtube-logo.svg";

const platformLogos = {
    ig: instagramLogo,
    yt: youtubeLogo
}
const CampaignPublic: React.FC = () => {
    /* Page parameters */
    const { campaignId } = useParams();
    const [searchParams] = useSearchParams();
    const referrer = searchParams.get('referrer');
    const [campaignPlatform, setCampaignPlatform] = useState<{ id: string, name: string } | null>(null);
    const languages = useSelector((state: RootState) => state.auth.master?.language_list) ?? {};
    const [showMore, setShowMore] = useState<{ label: string, values: string[] } | null>(null);

    if (referrer && campaignId) {
        const campaignReferrals = getLocalStorageItem('campaign_referrals');
        setLocalStorageItem('campaign_referrals', { ...campaignReferrals, [campaignId]: referrer });
    }

    const { isAuthenticated } = useSelector((state: RootState) => state.auth);
    /* Page parameters */

    /* Local States */
    const [campaignData, setCampaignData] = useState<CampaignData | null>(null);
    /* Local States */

    /* Hooks */
    const navigate = useNavigate();
    // const confirmationBox = useConfirmation();
    usePageTitle(campaignData?.campaign?.name ?? '')
    /* Hooks */


    /* Data handling functions */
    const fetchCampaignData = useCallback(async () => {
        if (!campaignId) return;
        const campaign_data = await fetchPublicCampaign(campaignId);
        setCampaignData(campaign_data);
    }, [campaignId])

    /* Data handling functions */

    /* Helper functions */
    const getCampaignImages = () => {
        let images: string[] = campaignData?.campaign.hero_img_url ? [campaignData.campaign.hero_img_url] : [];
        if (campaignData?.campaign.gallery_images)
            images = [...images, ...campaignData.campaign.gallery_images];

        return arrayOfUniqueStrings(images);
    }

    const shareURL = async () => {
        if (campaignData) {
            try {
                let share_url = campaignData?.share_url;
                const referralCode = (new URLSearchParams(window.location.search)).get('referrer');
                if (referralCode) {
                    share_url += '/?referrer=' + referralCode
                }
                await navigator.clipboard.writeText(share_url);
                toast.success('URL copied to your clipboard! 🙂');
            } catch (error) {
                console.error('Failed to copy text');
            }
        }
    }
    /* Helper functions */

    /* Lifecycle hooks */
    useEffect(() => {
        fetchCampaignData();
    }, [campaignId, fetchCampaignData])

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/campaign/' + campaignId);
        }
    }, [isAuthenticated, campaignId, navigate])

    useEffect(() => {
        if (campaignData) {
            const platformKey = campaignData.campaign.platform;
            const platform = platforms[platformKey as keyof typeof platforms];
            setCampaignPlatform(platform ?? null);
        }
    }, [campaignData])

    /* Lifecycle hooks */


    const specs = [
        {
            label: "Milestone",
            value: campaignData?.milestones?.[0]?.gig_type?.name,
        },
        toValidNumber(campaignData?.campaign?.barter_budget) > 0 && {
            label: "Reimbursement",
            value: campaignData?.campaign?.barter_reimbursement ? "Yes" : "No",
        },
        toValidNumber(campaignData?.campaign?.budget) > 0 && {
            label: "Earn Upto",
            value: `₹${campaignData?.campaign?.budget}`,
            textColor: 'text-success',
        },
        toValidNumber(campaignData?.campaign?.barter_budget) > 0 && {
            label: campaignData?.campaign?.barter_reimbursement ? "Reimbursement" : "Barter Value",
            value: `₹${campaignData?.campaign?.barter_budget}`,
            textColor: 'text-success',
        },
    ].filter(Boolean);

    const requirements = [
        campaignData?.campaign?.requirements?.follower_count?.min && {
            label: "Followers",
            value: `${campaignData?.campaign.requirements.follower_count.min}+`,
        },
        campaignData?.campaign?.requirements?.gender !== 'A' && {
            label: "Creator's Gender",
            value: campaignData?.campaign?.requirements?.gender_name,
        },

        campaignData?.campaign?.requirements?.age?.min && {
            label: "Creator's Age",
            value: `${campaignData.campaign?.requirements?.age?.min} - ${campaignData.campaign?.requirements?.age?.max}`,
        },

        (campaignData?.campaign?.requirements?.regions ?? [])?.length > 0 && {
            label: "Creator Region(s)",
            values: campaignData?.campaign?.requirements?.regions,
            isList: true,
        },

        (campaignData?.campaign?.requirements?.cities ?? [])?.length > 0 &&
        (!campaignData?.campaign?.requirements?.regions?.length) && {
            label: "Creator Location(s)",
            values: campaignData?.campaign?.requirements?.cities,
            isList: true,
        },

        campaignData?.campaign?.requirements?.audience_gender !== 'A' && {
            label: "Audience Gender",
            value: campaignData?.campaign.requirements?.audience_gender_name,
        },

        (campaignData?.campaign?.requirements?.audience_cities ?? [])?.length > 0 && {
            label: "Audience Location(s)",
            values: campaignData?.campaign?.requirements?.audience_cities,
            isList: true,
        },

        (campaignData?.campaign?.requirements?.audience_age_groups ?? [])?.length > 0 && {
            label: "Audience Age",
            values: campaignData?.campaign?.requirements?.audience_age_groups,
            isList: true,
        },

        (campaignData?.campaign?.requirements?.languages ?? [])?.length > 0 && {
            label: "Languages",
            values: campaignData?.campaign?.requirements?.languages?.map(
                (id) => languages[id]
            ),
            isList: true,
        },
    ].filter(Boolean);

    const renderSpec = (item: any) => (
        <div className="d-flex flex-column gap-1">
            {
                item.isMultiLine ? (
                    <div className="d-flex flex-column gap-0">
                        {
                            item.values.map((v: string, i: number) => (
                                <span key={i} className={`${item.textColor ?? 'text-dark'} fs-14 fw-500`}>{v}</span>
                            ))
                        }
                    </div>
                ) : (
                    item.isList ? (
                        <span className={`${item.textColor ?? 'text-dark'} fs-14 fw-500`}>{item.values.slice(0, 2).join(', ')}{item.values.length > 2 ? <span className="text-primary cursor-pointer" onClick={() => setShowMore({ label: item.label, values: item.values })}> +{item.values.length - 2}</span> : ''}</span>
                    ) : (
                        <span className={`${item.textColor ?? 'text-dark'} fs-14 fw-500`}>{item.value}</span>
                    )
                )
            }
            <span className="text-muted fs-12 fw-400">{item.label}</span>
        </div>
    );

    const getCampaignRequirements = () => {
        const rows = [];

        for (let i = 0; i < requirements.length; i += 2) {
            rows.push(
                <>
                    <CampaignSpecsRow
                        key={i}
                        col1={renderSpec(requirements[i])}
                        col2={requirements[i + 1] ? renderSpec(requirements[i + 1]) : <div />}
                    />
                    {i < requirements.length - 2 && <div className="divider m-0"></div>}
                </>
            );
        }
        return rows;
    }

    const getCampaignSpecs = () => {
        const rows = [];
        for (let i = 0; i < specs.length; i += 2) {
            rows.push(
                <>
                    <CampaignSpecsRow
                        key={i}
                        col1={renderSpec(specs[i])}
                        col2={specs[i + 1] ? renderSpec(specs[i + 1]) : <div />}
                    />
                    {i < specs.length - 2 && <div className="divider m-0"></div>}
                </>
            );
        }

        return rows;
    }


    /* Renders */
    if (!campaignData) return <Loader isLoading={true} />

    return (
        <>
            <div className="app-page mt-0 with-ticker mb-4">
                <div className="position-relative">
                    <CampaignImageSlider images={getCampaignImages()} />
                    <div className="sponsorship-card-platforms campaign-page">
                        {campaignData?.campaign?.platform === "ig" && (
                            <span className="sponsorship-platform-icon">
                                <img src={instagramLogo as unknown as string} alt="Instagram" className="sponsorship-platform-logo" />
                            </span>
                        )}
                        {campaignData?.campaign?.platform === "yt" && (
                            <span className="sponsorship-platform-icon">
                                <img src={youtubeLogo as unknown as string} alt="YouTube" className="sponsorship-platform-logo" />
                            </span>
                        )}
                    </div>
                </div>
                <div className="app-card my-2 py-18 px-3 d-flex flex-column gap-3">
                    <div>
                        <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="d-flex flex-column gap-2">
                                <span className="fs-18 fw-700 text-dark">{campaignData?.campaign.name}</span>
                                <span className="fs-16 text-muted fw-400">{campaignData?.brand?.category}</span>
                            </div>
                        </div>
                        <div className='d-flex flex-wrap gap-1 mt-2'>
                            {
                                (campaignData?.campaign?.speciality_niche_names ?? []).length > 0 ? (
                                    <>
                                        {
                                            campaignData.campaign.speciality_niche_names?.map((niche, index) => {
                                                return (
                                                    <span key={index} className="app-chip fs-12 fw-400 text-dark-gray">
                                                        {niche}
                                                    </span>
                                                );
                                            })
                                        }
                                    </>
                                ) : (
                                    <>
                                        {
                                            campaignData.campaign.niche_names?.map((niche, index) => {
                                                return (
                                                    <span key={index} className="app-chip">
                                                        {niche}
                                                    </span>
                                                );
                                            })
                                        }
                                    </>
                                )
                            }
                        </div>
                    </div>
                    <div className='app-card-wrapper box-shadow-none position-relative overflow-hidden'>
                        <div className="campaign-specs-card">
                            {getCampaignSpecs()}
                        </div>
                        {
                            toValidNumber(campaignData?.campaign?.barter_budget) > 0 &&
                            <div className="px-075 py-2">
                                <span className="fs-12 fw-400 text-dark">
                                    {
                                        campaignData?.campaign?.barter_reimbursement ? "You will have to buy product and it will be reimbursed by the brand." : "Brand will provide product for free."
                                    }
                                </span>

                            </div>
                        }
                    </div>
                    <div className="campaign-specs-card">
                        {getCampaignRequirements()}
                    </div>
                </div>
                <div className="app-card my-3 py-18 px-3 d-flex flex-column gap-075">
                    <h3 className="m-0 fs-16 fw-700 text-dark">Media Requirements</h3>
                    {
                        [...(campaignData?.milestones ?? [])].sort((a, b) => a.sort_order - b.sort_order).map((milestone, i) => (
                            <div className='specification-card-content' key={i}>
                                {
                                    milestone.gig_type.slug !== 'general' &&
                                    <h6 className="m-0 text-dark-gray fs-14 fw-400">{(campaignData.milestones ?? []).length > 1 ? `#${milestone.sort_order} ` : ''}{milestone.gig_type.name}</h6>
                                }
                                <div className="milestone-media-requirements">
                                    {
                                        campaignData?.campaign.platform === 'yt' &&
                                        <div className="milestone-media-requirement">
                                            <span>{milestone.content_media_types.VIDEO}</span>
                                            <span>Video</span>
                                        </div>
                                    }
                                    {
                                        campaignData?.campaign.platform === 'ig' &&
                                        <>
                                            <div className="milestone-media-requirement">
                                                <span>{milestone.content_media_types.REELS}</span>
                                                <span>Reel</span>
                                            </div>
                                            <div className="milestone-media-requirement">
                                                <span>{milestone.content_media_types.STORY}</span>
                                                <span>Story</span>
                                            </div>
                                            <div className="milestone-media-requirement">
                                                <span>{milestone.content_media_types.FEED}</span>
                                                <span>Post</span>
                                            </div>
                                        </>
                                    }
                                </div>
                            </div>
                        ))
                    }
                </div>
                <div className="app-card my-3 py-18 px-3 d-flex flex-column gap-075">
                    <h3 className="m-0 fs-16 fw-700 text-dark">Campaign Terms</h3>
                    {
                        [...(campaignData?.milestones ?? [])].sort((a, b) => a.sort_order - b.sort_order).map((milestone, i) => (
                            <div className='specification-card-content' key={i}>
                                <div className="milestone-media-requirements">
                                    {
                                        milestone.payment_days &&
                                        <div className="milestone-media-requirement">
                                            <span>{milestone.payment_days} <span className="subtext">day{milestone.payment_days > 1 ? 's' : ''}</span></span>
                                            <span>Payment</span>
                                        </div>
                                    }
                                    {
                                        milestone?.specifications?.content_rights_duration &&
                                        <div className="milestone-media-requirement">
                                            <span>{milestone.specifications.content_rights_duration} <span className='subtext'>month{Number(milestone.specifications.content_rights_duration) > 1 ? 's' : ''}</span></span>
                                            <span>Content Rights</span>
                                        </div>
                                    }
                                    {
                                        milestone?.specifications?.ad_rights_duration &&
                                        <div className="milestone-media-requirement">
                                            <span>{milestone.specifications.ad_rights_duration} <span className='subtext'>month{Number(milestone.specifications.ad_rights_duration) > 1 ? 's' : ''}</span></span>
                                            <span>Ad Rights</span>
                                        </div>
                                    }
                                </div>
                            </div>
                        ))
                    }
                </div>
                <div className="app-card my-3 py-18 px-3 d-flex flex-column gap-075">
                    <h3 className="m-0 fs-16 fw-700 text-dark">About Brand</h3>
                    <div>
                        {
                            campaignData.brand.website_url &&
                            <a className="" href={campaignData.brand.website_url} target="_blank" rel="noopener noreferrer">{campaignData.brand.website_url.split('?')[0]}</a>
                        }
                        <p className="text-dark-gray" style={{ whiteSpace: 'pre-wrap' }}>{campaignData.brand.description}</p>
                    </div>
                </div>
            </div>
            <div className="sticky-footer w-d-100">
                <Link to={"/campaign/" + campaignData.campaign.uuid} className="w-100 btn btn-primary">Join Us to Apply</Link>
            </div>
            <ShowMoreContentModal
                isActive={showMore !== null}
                onClose={() => setShowMore(null)}
                showMore={showMore}
            />
        </>

    );
}

export default CampaignPublic;